/**
 * Progress feedback while AI analysis / generation is running.
 * Cycles status messages and shows an indeterminate bar (reduced-motion safe).
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './AnalyzingProgress.css';

const AnalyzingProgress = ({
  active,
  title,
  steps,
  intervalMs = 2800,
  className = '',
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const rootRef = useRef(null);
  const safeSteps = Array.isArray(steps) && steps.length > 0 ? steps : [title];

  useEffect(() => {
    if (!active) {
      setStepIndex(0);
      return undefined;
    }
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';
    window.scrollTo({ top: 0, left: 0, behavior });
    rootRef.current?.scrollIntoView({ block: 'start', behavior });
    if (safeSteps.length < 2) {
      return undefined;
    }
    const id = setInterval(() => {
      setStepIndex((prev) => (prev + 1) % safeSteps.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs, safeSteps.length]);

  if (!active) {
    return null;
  }

  const current = safeSteps[stepIndex] || title;

  return (
    <div
      ref={rootRef}
      className={`analyzing-progress ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="analyzing-progress__row">
        <span className="analyzing-progress__spinner" aria-hidden="true" />
        <div className="analyzing-progress__copy">
          {title ? <p className="analyzing-progress__title">{title}</p> : null}
          <p className="analyzing-progress__step">{current}</p>
        </div>
      </div>
      <div className="analyzing-progress__track" aria-hidden="true">
        <div className="analyzing-progress__bar" />
      </div>
      <ol className="analyzing-progress__dots" aria-hidden="true">
        {safeSteps.map((label, index) => (
          <li
            key={label}
            className={
              index === stepIndex
                ? 'analyzing-progress__dot is-active'
                : index < stepIndex
                  ? 'analyzing-progress__dot is-done'
                  : 'analyzing-progress__dot'
            }
          />
        ))}
      </ol>
    </div>
  );
};

AnalyzingProgress.propTypes = {
  active: PropTypes.bool.isRequired,
  title: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.string),
  intervalMs: PropTypes.number,
  className: PropTypes.string,
};

AnalyzingProgress.defaultProps = {
  title: '',
  steps: [],
  intervalMs: 2800,
  className: '',
};

export default AnalyzingProgress;
